import React, { useState } from 'react';
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { cardAnimationVariants } from '../../../../BeFi20/BeFi20.meta';
import { Text } from '../../../../CommonComponents/OPL/Text/Text';
import { Box } from '../../../../CommonComponents/OPL/Box/Box';
import { Category } from './components/Category/Category';
import { SortableCategory } from './components/SortableCategory/SortableCategory';
import { StepCard } from '../../../PulseCheck.styles';
import { useWorkflowContext } from '../../context/useWorkflowContext';
import { CategoryType } from '../../../api/useWorkflowQuery';
import { StepWrapper } from '../../../components/StepWrapper/StepWrapper';

export const CardSorting = () => {
  const {
    categories,
    setCategories,
    showCategoryNumbers,
    setShowCategoryNumbers,
  } = useWorkflowContext();

  const [activeCategory, setActiveCategory] = useState<CategoryType | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleDragStart = (event: DragStartEvent) => {
    const { active } = event;

    const foundCategory = categories.find(({ id }) => id === active.id);

    if (!foundCategory) {
      throw new Error(`Could not find category with id ${active.id}`);
    }

    setActiveCategory(foundCategory);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    setActiveCategory(null);
    setShowCategoryNumbers(true);

    const { active, over } = event;

    if (over && active.id !== over.id) {
      setCategories(newCategories => {
        const oldIndex = newCategories.findIndex(({ id }) => id === active.id);
        const newIndex = newCategories.findIndex(({ id }) => id === over.id);

        return arrayMove(newCategories, oldIndex, newIndex);
      });
    }
  };

  return (
    <StepWrapper>
      <StepCard variants={cardAnimationVariants} initial="enter" animate="visible">
        <Text as="h1" fontSize="h4" fontWeight={600} textAlign="center" mt="24" mb="16">
          What is most important to you?
        </Text>

        <Text as="p" fontSize="h6" color="neutral.500" textAlign="center" mb="24">
          Drag & drop the six fulfillment categories in order of most importance to least
          importance.
        </Text>

        <Box display="flex" flexDirection="column" css="gap: 8px">
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
          >
            <SortableContext items={categories} strategy={verticalListSortingStrategy}>
              {categories.map((category, index) => (
                <SortableCategory
                  key={category.id}
                  category={category}
                  number={showCategoryNumbers && !activeCategory ? index + 1 : undefined}
                />
              ))}
            </SortableContext>

            <DragOverlay>
              {activeCategory ? <Category category={activeCategory} isDragging /> : null}
            </DragOverlay>
          </DndContext>
        </Box>
      </StepCard>
    </StepWrapper>
  );
};
